import { tokens } from "../styles/tokens";

/**
 * Dwell-density heat field (ADR 104). Replaces the canvas-blob heatmap: poses
 * are splatted at full WS rate (~11 Hz) into a 0.25 m float grid with a small
 * Gaussian kernel, each sample weighted by the wall-clock time it represents —
 * so the field reads "seconds spent per cell", independent of sample rate.
 *
 * A 30-minute rolling window is kept exactly with one grid bucket per minute
 * plus a running sum, so eviction never rescans a point list. Colors come from
 * the design-system viridis ramp with percentile + sqrt compression, which is
 * what keeps single passes visible next to a parked hotspot.
 */

export const HEAT_STOPS: readonly string[] = tokens.heat;

const KERNEL_SIGMA_M = 0.45;   // ≈ forklift half-width; FWHM ~1.05 m
const KERNEL_CUTOFF_SIGMAS = 2.5;
const WINDOW_MINUTES = 30;     // parity with the old live accumulator horizon
/** Continuous dwell (s) at one spot that saturates the ramp when traffic is light. */
const FULL_SCALE_DWELL_S = 90;
const GAMMA = 0.5;             // sqrt compression: low densities keep visible nuance
/** Faintest nonzero density maps ≥10% up the ramp — heat-0 pure is unreadable on the navy floor. */
const LUT_FLOOR = 26;

const MS_PER_MIN = 60_000;

export class HeatField {
  readonly gw: number;
  readonly gh: number;
  readonly cellM: number;
  /** World coordinates of grid cell (0,0). Non-zero for any floor that does not
   *  start at the origin -- see the constructor note. */
  readonly originX: number;
  readonly originY: number;

  private readonly sum: Float32Array;
  private readonly buckets: Float32Array[];
  private readonly stamps: number[];         // minute index held by each bucket, -1 = empty
  private lastMinute = -1;

  private readonly kR: number;               // kernel radius in cells
  private readonly kernel: Float32Array;     // (2kR+1)² weights, Σ = 1
  private readonly kernelPeak: number;

  /**
   * The grid covers [originX, originX + widthM) x [originY, originY + heightM).
   *
   * The origin is not decoration: a real site's coordinates need not start at
   * zero, and wh-webinar's floor sits at x 112..414, y -386..-87. A grid pinned
   * to (0,0) drops every one of those splats on the floor of `splat`, and the
   * heatmap renders blank while every other layer looks fine. This is ADR 204's
   * bug -- the camera aiming at the demo seed's 60x40 -- in the one layer that
   * did not get the fix.
   */
  constructor(widthM: number, heightM: number, cellM = 0.25, originX = 0, originY = 0) {
    this.cellM = cellM;
    this.originX = originX;
    this.originY = originY;
    this.gw = Math.max(1, Math.round(widthM / cellM));
    this.gh = Math.max(1, Math.round(heightM / cellM));
    this.sum = new Float32Array(this.gw * this.gh);
    this.buckets = Array.from({ length: WINDOW_MINUTES }, () => new Float32Array(this.gw * this.gh));
    this.stamps = new Array(WINDOW_MINUTES).fill(-1);

    const sigmaC = KERNEL_SIGMA_M / cellM;
    this.kR = Math.ceil(KERNEL_CUTOFF_SIGMAS * sigmaC);
    const side = 2 * this.kR + 1;
    this.kernel = new Float32Array(side * side);
    let total = 0;
    for (let dy = -this.kR; dy <= this.kR; dy++) {
      for (let dx = -this.kR; dx <= this.kR; dx++) {
        const w = Math.exp(-(dx * dx + dy * dy) / (2 * sigmaC * sigmaC));
        this.kernel[(dy + this.kR) * side + (dx + this.kR)] = w;
        total += w;
      }
    }
    for (let i = 0; i < this.kernel.length; i++) this.kernel[i] /= total;
    this.kernelPeak = this.kernel[this.kR * side + this.kR];
  }

  reset(): void {
    this.sum.fill(0);
    for (const b of this.buckets) b.fill(0);
    this.stamps.fill(-1);
    this.lastMinute = -1;
  }

  /** Evict buckets that fell out of the window. Cheap when the minute hasn't changed. */
  private rotate(nowMs: number): void {
    const minute = Math.floor(nowMs / MS_PER_MIN);
    if (minute === this.lastMinute) return;
    this.lastMinute = minute;
    for (let s = 0; s < WINDOW_MINUTES; s++) {
      const stamp = this.stamps[s];
      if (stamp < 0 || minute - stamp < WINDOW_MINUTES) continue;
      const b = this.buckets[s];
      for (let i = 0; i < b.length; i++) {
        if (b[i] !== 0) this.sum[i] = Math.max(0, this.sum[i] - b[i]);
      }
      b.fill(0);
      this.stamps[s] = -1;
    }
  }

  /** Deposit `weightSec` of dwell around (x, y) meters. Out-of-bounds centers are dropped. */
  splat(x: number, y: number, weightSec: number, nowMs: number): void {
    this.rotate(nowMs);
    const cx = Math.floor((x - this.originX) / this.cellM);
    const cy = Math.floor((y - this.originY) / this.cellM);
    if (cx < 0 || cy < 0 || cx >= this.gw || cy >= this.gh || weightSec <= 0) return;

    const minute = Math.floor(nowMs / MS_PER_MIN);
    const slot = minute % WINDOW_MINUTES;
    // rotate() already evicted anything ≥ WINDOW_MINUTES old, so this slot is
    // either empty or already holds the current minute.
    this.stamps[slot] = minute;
    const bucket = this.buckets[slot];

    const side = 2 * this.kR + 1;
    for (let dy = -this.kR; dy <= this.kR; dy++) {
      const ty = cy + dy;
      if (ty < 0 || ty >= this.gh) continue;
      const rowK = (dy + this.kR) * side;
      const rowT = ty * this.gw;
      for (let dx = -this.kR; dx <= this.kR; dx++) {
        const tx = cx + dx;
        if (tx < 0 || tx >= this.gw) continue;
        const w = this.kernel[rowK + (dx + this.kR)] * weightSec;
        bucket[rowT + tx] += w;
        this.sum[rowT + tx] += w;
      }
    }
  }

  /** Dwell seconds accumulated at (x, y) meters, window applied. */
  valueAt(x: number, y: number, nowMs: number): number {
    this.rotate(nowMs);
    const cx = Math.floor((x - this.originX) / this.cellM);
    const cy = Math.floor((y - this.originY) / this.cellM);
    if (cx < 0 || cy < 0 || cx >= this.gw || cy >= this.gh) return 0;
    return this.sum[cy * this.gw + cx];
  }

  /** Total dwell mass in the window — test/diagnostic helper. */
  totalMass(nowMs: number): number {
    this.rotate(nowMs);
    let t = 0;
    for (let i = 0; i < this.sum.length; i++) t += this.sum[i];
    return t;
  }

  /**
   * Write the field as RGBA8 through `lut`. Normalization: p99 of the nonzero
   * cells, floored at FULL_SCALE_DWELL_S of continuous single-spot dwell, then
   * sqrt. Returns false when the field is empty. `flipRows` writes row 0 last
   * (typed-array texture uploads ignore THREE's flipY).
   */
  colorize(out: Uint8ClampedArray, lut: Uint8ClampedArray, nowMs: number, flipRows = false): boolean {
    this.rotate(nowMs);
    const n = this.sum.length;
    const nonzero: number[] = [];
    for (let i = 0; i < n; i++) if (this.sum[i] > 0) nonzero.push(this.sum[i]);
    if (nonzero.length === 0) {
      out.fill(0);
      return false;
    }
    nonzero.sort((a, b) => a - b);
    const p99 = nonzero[Math.floor(0.99 * (nonzero.length - 1))];
    const scale = Math.max(p99, FULL_SCALE_DWELL_S * this.kernelPeak);

    for (let row = 0; row < this.gh; row++) {
      const srcRow = row * this.gw;
      const dstRow = (flipRows ? this.gh - 1 - row : row) * this.gw;
      for (let col = 0; col < this.gw; col++) {
        const v = this.sum[srcRow + col];
        const o = (dstRow + col) * 4;
        if (v <= 0) {
          out[o] = out[o + 1] = out[o + 2] = out[o + 3] = 0;
          continue;
        }
        const t = Math.pow(Math.min(v / scale, 1), GAMMA);
        const li = Math.max(LUT_FLOOR, Math.round(t * 255)) * 4;
        out[o] = lut[li];
        out[o + 1] = lut[li + 1];
        out[o + 2] = lut[li + 2];
        out[o + 3] = lut[li + 3];
      }
    }
    return true;
  }
}

/** 256-entry RGBA LUT interpolated across the token ramp; index 0 transparent. */
export function buildHeatLut(stops: readonly string[]): Uint8ClampedArray {
  const rgb = stops.map((hex) => {
    const v = parseInt(hex.slice(1), 16);
    return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
  });
  const lut = new Uint8ClampedArray(256 * 4);
  for (let i = 1; i < 256; i++) {
    const t = i / 255;
    const seg = Math.min(t * (rgb.length - 1), rgb.length - 1 - 1e-6);
    const k = Math.floor(seg);
    const f = seg - k;
    lut[i * 4] = Math.round(rgb[k][0] + (rgb[k + 1][0] - rgb[k][0]) * f);
    lut[i * 4 + 1] = Math.round(rgb[k][1] + (rgb[k + 1][1] - rgb[k][1]) * f);
    lut[i * 4 + 2] = Math.round(rgb[k][2] + (rgb[k + 1][2] - rgb[k][2]) * f);
    lut[i * 4 + 3] = Math.round(120 + 105 * t);
  }
  return lut;
}

/**
 * Converts per-asset lastSeen timestamps into dwell weights: each accepted
 * sample is worth the wall-clock gap since the previous one (clamped to 0.5 s
 * so WS hiccups don't paint dwell that wasn't observed), making density
 * independent of the delivery rate.
 */
export class DwellTracker {
  private readonly last = new Map<string, number>();
  private static readonly NOMINAL_S = 1 / 11; // one 11 Hz frame
  private static readonly MAX_GAP_S = 0.5;

  record(assetId: string, lastSeenMs: number): number | null {
    const prev = this.last.get(assetId);
    if (prev !== undefined && lastSeenMs <= prev) return null;
    this.last.set(assetId, lastSeenMs);
    if (prev === undefined) return DwellTracker.NOMINAL_S;
    return Math.min((lastSeenMs - prev) / 1000, DwellTracker.MAX_GAP_S);
  }

  reset(): void {
    this.last.clear();
  }
}

// ── Live singleton ─────────────────────────────────────────────────────────
// One field per page lifetime, fed from the telemetry store at delta rate and
// read by HeatmapLayer. Kept here (not in a React hook) so accumulation runs
// while the heatmap toggle is off — flipping it on shows history immediately.

let liveField: HeatField | null = null;
let liveDims = "";
let liveSeeded = false;
const liveTracker = new DwellTracker();

/** telemetry_raw rows per second per asset (nominal edge pose rate): count → seconds. */
export const SINK_HZ = 11;

export function getLiveHeatField(
  widthM: number, heightM: number, originX = 0, originY = 0,
): HeatField {
  // The origin is part of the identity: the floor frame arrives asynchronously
  // (zones fetch), so the first call of a session may well carry the fallback
  // frame. Keying on dims alone would keep that stale grid for the whole
  // session and silently swallow every splat.
  const dims = `${widthM}x${heightM}@${originX},${originY}`;
  if (!liveField || liveDims !== dims) {
    liveField = new HeatField(widthM, heightM, 0.25, originX, originY);
    liveDims = dims;
    liveTracker.reset();
    liveSeeded = false;
  }
  return liveField;
}

export function resetLiveHeat(): void {
  liveField?.reset();
  liveTracker.reset();
  liveSeeded = false;
}

/**
 * One-shot claim for backfilling the live field from ClickHouse history —
 * without it a fresh tab starts blank and the last 30 min of traffic only
 * exist server-side. First caller per field instance wins.
 */
export function claimLiveHeatSeed(): boolean {
  if (liveSeeded) return false;
  liveSeeded = true;
  return true;
}

const STALE_MS = 5_000;

/** Feed one batch of live poses; call at delta rate — dedup/weighting is internal. */
export function recordLivePoses(
  field: HeatField,
  poses: Array<{ id: string; x: number; y: number; lastSeenMs?: number }>,
  nowMs: number,
): void {
  for (const p of poses) {
    if (!p.lastSeenMs || nowMs - p.lastSeenMs > STALE_MS) continue;
    const w = liveTracker.record(p.id, p.lastSeenMs);
    if (w !== null && w > 0) field.splat(p.x, p.y, w, p.lastSeenMs);
  }
}
